import {
  Component,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ContextMenuOption } from '../models/context-menu-option';
import { PdfType } from '../models/pdf-type';
import { UploadItem } from '../models/upload-item';
import {
  ConstructionSite,
  ConstructionSiteState,
  NodeItem,
} from '../../api/models';
import { FileTreeComponent } from '../file-tree/file-tree.component';
import { StateService } from '../../services/state.service';
import { ToolService } from '../../services/tool.service';
import { RouterService } from '../../services/router.service';
import { CameraService } from '../../services/camera.service';
import { ModalService } from '../../services/modal.service';

@Component({
  selector: 'lib-construction-sites',
  templateUrl: './construction-sites.component.html',
  styleUrls: ['./construction-sites.component.scss'],
  standalone: false,
})
export class ConstructionSitesComponent implements OnInit {
  private translate = inject(TranslateService);

  private sub: Subscription;
  public conSite: ConstructionSite;
  public selectedNodeItem: NodeItem;
  public detailsOpened: boolean = true;
  @ViewChild(FileTreeComponent) child!: FileTreeComponent;
  public currentFolder = signal<NodeItem>({});
  public selectedNodeItems: NodeItem[] = [];
  @Output()
  public addButtonVisibilityEvent: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  constructor(
    public state: StateService,
    public tools: ToolService,
    public routerService: RouterService,
    private cameraService: CameraService,
    private modalService: ModalService
  ) {}

  public currentFolderNodeItemChanged(item: NodeItem) {
    this.selectedNodeItem = item;
    this.currentFolder.update((n) => item);
    this.addButtonVisibilityEvent.emit(
      this.currentFolder()?.id == null &&
        this.state.isOfficeUser &&
        this.selectedNodeItems.length == 0
    );
  }

  selectedNodeItemsChanged(items: NodeItem[]) {
    this.selectedNodeItems = items;
  }

  ngOnInit() {
    this.sub = this.state.selectedConSite$.subscribe((x) => (this.conSite = x));
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  openPhone(phone: string) {
    window.open(`tel:${phone}`, '_system');
  }

  openMail(mail: string) {
    window.open(`mailto:${mail}`, '_system');
  }

  async openFile(event: { ids: string[]; index: number }) {
    await this.state.openFile(event.ids, PdfType.Document, event.index);
  }

  async openContextMenu(event: MouseEvent) {
    let additionalOptions: ContextMenuOption[] = [
      {
        icon: 'map-outline',
        label: this.translate.instant('constructionSites.openAddress'),
        value: 'maps',
      },
    ];

    if (this.conSite.phone1)
      additionalOptions.push({
        icon: 'call-outline',
        label: this.conSite.contactName1
          ? this.translate.instant('constructionSites.call', {
              name: this.conSite.contactName1,
            })
          : this.conSite.phone1,
        value: 'call1',
      });
    if (this.conSite.email1)
      additionalOptions.push({
        icon: 'mail-outline',
        label: this.translate.instant('constructionSites.email', {
          email: this.conSite.email1,
        }),
        value: 'mail1',
      });
    if (this.conSite.phone2)
      additionalOptions.push({
        icon: 'call-outline',
        label: this.conSite.contactName2
          ? this.translate.instant('constructionSites.call', {
              name: this.conSite.contactName2,
            })
          : this.conSite.phone2,
        value: 'call2',
      });
    if (this.conSite.email2)
      additionalOptions.push({
        icon: 'mail-outline',
        label: this.translate.instant('constructionSites.email', {
          email: this.conSite.email2,
        }),
        value: 'mail2',
      });
    const { data, role } = await this.state.openPopover(
      event,
      true,
      additionalOptions
    );
    console.log('Popover dismissed with role:', role, 'and data:', data);
    switch (data) {
      case 'edit': {
        this.state.openConstructionSiteAlert(this.conSite);
        break;
      }
      case 'delete': {
        await this.state.deleteConstructionSite(this.conSite.id);
        break;
      }
      case 'call1': {
        this.openPhone(this.conSite.phone1);
        break;
      }
      case 'call2': {
        this.openPhone(this.conSite.phone2);
        break;
      }
      case 'mail1': {
        this.openMail(this.conSite.email1);
        break;
      }
      case 'mail2': {
        this.openMail(this.conSite.email2);
        break;
      }
      case 'maps': {
        this.openMap();
        break;
      }
    }
  }

  openMap() {
    const query = encodeURIComponent(
      this.conSite.address +
        ', ' +
        this.conSite.postalCode +
        ' ' +
        this.conSite.city
    );
    this.tools.openMap(query);
  }

  goBack() {
    this.child.goBack();
  }

  archiveConSites() {
    this.state.changeConstructionSiteStates(
      this.child.selectedNodeItems.map((x) => x.constructionSiteId),
      ConstructionSiteState.$1
    );
    this.child.unselectAll();
  }

  activateConSites() {
    this.state.changeConstructionSiteStates(
      this.child.selectedNodeItems.map((x) => x.constructionSiteId),
      ConstructionSiteState.$0
    );
    this.child.unselectAll();
  }

  openFileInput() {
    document.getElementById('file').click();
  }

  public async fileInput(files: FileList) {
    await this.modalService.openUploadModal(null, this.currentFolder(), files);
  }

  public async openCamera() {
    const blobs = await this.cameraService.getPhotos();
    if (blobs.length === 0) {
      return;
    }
    const date = new Date().toLocaleDateString('de-DE', this.tools.options);
    const items: UploadItem[] = blobs.map((blob, i) => ({
      blob,
      displayName: 'Foto vom ' + date + (blobs.length > 1 ? ` (${i + 1})` : ''),
      fileType: '.' + (blob.type.split('/')[1] || 'jpeg'),
    }));

    await this.modalService.openUploadModal(items, this.currentFolder(), null);
  }
}
